import {useMemo} from "react";

import {RenderState, useRenderState} from "@khanacademy/wonder-blocks-core";
import {PermissionsInfo, RequestInfo} from "@khan/shared-core";

import {RegionOverrideSource, type RegionOverrideResult} from "./types.ts";
import {useUserSettingsRegion} from "./use-user-settings-region.ts";

const unknownResult = {
    status: "success",
    // ZZ is the default value which means "Unknown Region".
    regionCode: "ZZ",
    // TODO(CP-7859) Change back to RegionOverrideSource.NONE
    regionSource: RegionOverrideSource.IP_GEOLOCATION,
} as RegionOverrideResult;
const noDataResult = {status: "no-data"} as RegionOverrideResult;

// Implements the region override logic from ADR #616 to determine what region
// the user wishes to see standards and the course menu for. Should not be used
// to render pages on the server without shouldSSR: true as it can otherwise
// rely on user data which currently cannot be edge cached.
// https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/2174386279/ADR+616+Region+Aware+Standards#Decision
export const useRegionOverride = (
    shouldSSR?: boolean,
): RegionOverrideResult => {
    const userRegionResult = useUserSettingsRegion();
    const renderState = useRenderState();

    const shouldReturnNoDataResult =
        // Return a no-data a result during server render for logged out users
        // unless it's requested. For example, the logged-out homepage should
        // server render and edge cache based only on KA-Region.
        (renderState === RenderState.Initial && !shouldSSR) ||
        // Return a no-data result during server render for logged in users as
        // we want to wait until we get the user query back on the client.
        (renderState === RenderState.Initial && PermissionsInfo.isLoggedIn);

    const shouldFallbackToIPGeolocation =
        // Avoid accessing RequestInfo during server render for logged out users
        // unless requested as doing so shards the edge cache on KA-Region.
        // Avoid accessing RequestInfo during server render for logged in users
        // as we want to wait until we get the user query back on the client.
        !shouldReturnNoDataResult &&
        // Avoid accessing RequestInfo until we know the result of the user
        // query.
        userRegionResult.status === "success" &&
        // Avoid accessing RequestInfo when user query returns a known
        // region.
        userRegionResult.regionCode === "ZZ";

    const resultFromRequestInfo = useMemo(() => {
        // Avoid accessing RequestInfo unless necessary.
        if (!shouldFallbackToIPGeolocation) {
            return noDataResult;
        }

        // This should never happen given RequestInfo.kaRegion has a default of
        // "ZZ".
        if (!RequestInfo.kaRegion) {
            return unknownResult;
        }

        return {
            status: "success",
            regionCode: RequestInfo.kaRegion,
            regionSource: RegionOverrideSource.IP_GEOLOCATION,
        } as RegionOverrideResult;
    }, [shouldFallbackToIPGeolocation]);

    if (shouldReturnNoDataResult) {
        return noDataResult;
    }

    if (!shouldFallbackToIPGeolocation) {
        return userRegionResult;
    }

    // When no other overrides are present, fall back to IP Geolocation.
    return resultFromRequestInfo;
};
